import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileInvoiceDollar, faSave, faSpinner, faWarehouse, faCheck } from '@fortawesome/free-solid-svg-icons';
import { UI_COLORS, colorHelpers } from '../utils/colors.jsx';
import { useRoleAccess } from '../hooks/useRoleAccess';
import { USER_ROLES } from '../utils/constants';
import { useMessageAlert } from '../components/common/MessageAlert';
import useWarehouses from '../hooks/useWarehouses';
import { useTranslation } from 'react-i18next';

const MAX_LEN = 200;

const initialEditsFor = (warehouse) => ({
  sales_account_ledger: warehouse.sales_account_ledger ?? '',
  sales_voucher_type_name: warehouse.sales_voucher_type_name ?? '',
  purchase_account_ledger: warehouse.purchase_account_ledger ?? '',
  purchase_voucher_type_name: warehouse.purchase_voucher_type_name ?? '',
});

const normalizeForCompare = (v) => (v === null || v === undefined ? '' : String(v));

const TallySettingsPage = () => {
  const { t } = useTranslation();
  const TS = t('tallySettings', { returnObjects: true });
  const CS = t('common', { returnObjects: true });
  const TALLY_FIELDS = [
    { key: 'sales_account_ledger', ...TS.fields.salesAccountLedger },
    { key: 'sales_voucher_type_name', ...TS.fields.salesVoucherType },
    { key: 'purchase_account_ledger', ...TS.fields.purchaseAccountLedger },
    { key: 'purchase_voucher_type_name', ...TS.fields.purchaseVoucherType },
  ];
  const { userRole } = useRoleAccess();
  const isSuperAdmin = userRole === USER_ROLES.SUPER_ADMIN;
  const { showSuccess, showError } = useMessageAlert();

  const { warehouses, loading, fetchWarehouses, updateWarehouse } = useWarehouses();

  const [edits, setEdits] = useState({}); // { [warehouseId]: { field: value } }
  const [savingId, setSavingId] = useState(null);
  const [lastSavedAt, setLastSavedAt] = useState({}); // { [warehouseId]: timestamp }

  useEffect(() => {
    fetchWarehouses({ skip: 0, limit: 100 }, { force: true });
  }, [fetchWarehouses]);

  // Seed edits from warehouses once they arrive (only for warehouses we haven't touched)
  useEffect(() => {
    if (!Array.isArray(warehouses) || warehouses.length === 0) return;
    setEdits((prev) => {
      const next = { ...prev };
      warehouses.forEach((w) => {
        if (!next[w.id]) next[w.id] = initialEditsFor(w);
      });
      return next;
    });
  }, [warehouses]);

  const handleChange = (warehouseId, field, value) => {
    // Enforce max length in the input itself
    const clipped = value.length > MAX_LEN ? value.slice(0, MAX_LEN) : value;
    setEdits((prev) => ({
      ...prev,
      [warehouseId]: {
        ...(prev[warehouseId] || {}),
        [field]: clipped,
      },
    }));
  };

  const isRowDirty = useCallback((warehouse) => {
    const current = edits[warehouse.id];
    if (!current) return false;
    return TALLY_FIELDS.some(({ key }) => normalizeForCompare(current[key]) !== normalizeForCompare(warehouse[key]));
  }, [edits]);

  const handleSave = async (warehouse) => {
    if (!isSuperAdmin) return;
    const current = edits[warehouse.id];
    if (!current) return;

    // Build partial payload: only send fields that actually changed.
    // Send empty string as null to clear a stored value (matches backend contract).
    const payload = {};
    TALLY_FIELDS.forEach(({ key }) => {
      const nextVal = current[key];
      const origVal = warehouse[key];
      if (normalizeForCompare(nextVal) !== normalizeForCompare(origVal)) {
        payload[key] = nextVal === '' ? null : nextVal;
      }
    });

    if (Object.keys(payload).length === 0) return;

    setSavingId(warehouse.id);
    try {
      const res = await updateWarehouse(warehouse.id, payload);
      if (res && res.success) {
        setLastSavedAt((prev) => ({ ...prev, [warehouse.id]: Date.now() }));
        showSuccess(TS.notifications.saved.replace('{name}', warehouse.name || TS.warehouseFallbackGeneric));
      } else {
        showError(res?.error || TS.notifications.saveFailed);
      }
    } catch (err) {
      showError(err?.response?.data?.error || err?.message || TS.notifications.saveFailed);
    } finally {
      setSavingId(null);
    }
  };

  const handleReset = (warehouse) => {
    setEdits((prev) => ({ ...prev, [warehouse.id]: initialEditsFor(warehouse) }));
  };

  const sortedWarehouses = useMemo(() => {
    if (!Array.isArray(warehouses)) return [];
    return [...warehouses].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [warehouses]);

  return (
    <div className="p-6 max-w-6xl">
      <div className="mb-6">
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.INFO, 0.12) }}
          >
            <FontAwesomeIcon icon={faFileInvoiceDollar} style={{ color: UI_COLORS.STATUS.INFO }} />
          </div>
          <div>
            <h1 className="text-2xl font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
              {TS.title}
            </h1>
            <p className="mt-1 text-sm" style={{ color: UI_COLORS.TEXT.MUTED }}>
              {TS.subtitle}
            </p>
          </div>
        </div>

        {!isSuperAdmin && (
          <div
            className="mt-4 px-4 py-2 rounded-lg text-sm"
            style={{
              backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.WARNING, 0.1),
              color: UI_COLORS.STATUS.WARNING,
            }}
          >
            {TS.superAdminOnlyBanner}
          </div>
        )}
      </div>

      {loading && sortedWarehouses.length === 0 ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-40 rounded-xl bg-gray-100 animate-pulse" />
          ))}
        </div>
      ) : sortedWarehouses.length === 0 ? (
        <div
          className="p-8 rounded-xl text-center text-sm"
          style={{
            backgroundColor: colorHelpers.withOpacity(UI_COLORS.BACKGROUNDS.SECTION, 0.5),
            color: UI_COLORS.TEXT.MUTED,
          }}
        >
          {TS.noWarehousesFound}
        </div>
      ) : (
        <div className="space-y-5">
          {sortedWarehouses.map((warehouse) => {
            const current = edits[warehouse.id] || initialEditsFor(warehouse);
            const dirty = isRowDirty(warehouse);
            const isSaving = savingId === warehouse.id;
            const savedRecently = lastSavedAt[warehouse.id] && Date.now() - lastSavedAt[warehouse.id] < 5000;
            const allEmpty = TALLY_FIELDS.every(({ key }) => !warehouse[key]);

            return (
              <div
                key={warehouse.id}
                className="rounded-xl border p-5"
                style={{
                  backgroundColor: colorHelpers.withOpacity(UI_COLORS.BACKGROUNDS.SECTION, 0.4),
                  borderColor: dirty
                    ? colorHelpers.withOpacity(UI_COLORS.STATUS.INFO, 0.4)
                    : colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.2),
                }}
              >
                <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
                  <div className="flex items-center gap-3">
                    <div
                      className="w-9 h-9 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: colorHelpers.withOpacity(UI_COLORS.NAVIGATION.ACTIVE, 0.12) }}
                    >
                      <FontAwesomeIcon icon={faWarehouse} style={{ color: UI_COLORS.NAVIGATION.ACTIVE }} />
                    </div>
                    <div>
                      <div className="font-semibold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                        {warehouse.name || TS.warehouseFallback.replace('{id}', String(warehouse.id))}
                      </div>
                      <div className="text-xs" style={{ color: UI_COLORS.TEXT.MUTED }}>
                        {warehouse.state || warehouse.address || '—'}
                        {allEmpty && (
                          <span
                            className="ml-2 inline-block px-2 py-0.5 rounded text-[10px] font-semibold"
                            style={{
                              backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.WARNING, 0.15),
                              color: UI_COLORS.STATUS.WARNING,
                            }}
                          >
                            {TS.notConfiguredBadge}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {isSuperAdmin && (
                    <div className="flex items-center gap-2">
                      {dirty && (
                        <button
                          type="button"
                          onClick={() => handleReset(warehouse)}
                          disabled={isSaving}
                          className="px-3 py-1.5 rounded-md text-sm font-medium border transition-colors disabled:opacity-50"
                          style={{ borderColor: UI_COLORS.BORDERS.MEDIUM, color: UI_COLORS.TEXT.PRIMARY }}
                        >
                          {CS.actions.reset}
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => handleSave(warehouse)}
                        disabled={isSaving || !dirty}
                        className="inline-flex items-center gap-2 px-4 py-1.5 rounded-md text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{
                          backgroundColor: UI_COLORS.BUTTONS.PRIMARY.background,
                          color: UI_COLORS.BUTTONS.PRIMARY.text,
                        }}
                      >
                        <FontAwesomeIcon
                          icon={isSaving ? faSpinner : (savedRecently && !dirty ? faCheck : faSave)}
                          className={isSaving ? 'animate-spin' : ''}
                        />
                        {isSaving ? CS.actions.saving : (savedRecently && !dirty ? CS.actions.saved : CS.actions.save)}
                      </button>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {TALLY_FIELDS.map(({ key, label, placeholder }) => {
                    const value = current[key] ?? '';
                    const origVal = normalizeForCompare(warehouse[key]);
                    const fieldDirty = normalizeForCompare(value) !== origVal;

                    return (
                      <div key={key}>
                        <label
                          className="block text-xs font-semibold mb-1"
                          style={{ color: UI_COLORS.TEXT.MUTED }}
                        >
                          {label}
                          {fieldDirty && (
                            <span
                              className="ml-2 inline-block w-1.5 h-1.5 rounded-full align-middle"
                              style={{ backgroundColor: UI_COLORS.STATUS.INFO }}
                              aria-label={TS.ariaUnsavedChange}
                            />
                          )}
                        </label>
                        <input
                          type="text"
                          value={value}
                          onChange={(e) => handleChange(warehouse.id, key, e.target.value)}
                          disabled={!isSuperAdmin || isSaving}
                          maxLength={MAX_LEN}
                          placeholder={placeholder}
                          className="w-full px-3 py-2 rounded-md border text-sm focus:outline-none focus:ring-2 disabled:bg-gray-50 disabled:cursor-not-allowed"
                          style={{
                            borderColor: fieldDirty ? UI_COLORS.STATUS.INFO : UI_COLORS.FORMS.INPUT.border,
                            backgroundColor: UI_COLORS.FORMS.INPUT.background,
                            borderLeftWidth: fieldDirty ? 3 : 1,
                          }}
                        />
                        <div className="mt-1 text-[10px]" style={{ color: UI_COLORS.TEXT.MUTED }}>
                          {value.length}/{MAX_LEN} · {TS.charCounterSuffix}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TallySettingsPage;
